<script setup lang="ts">
import { VdSelect, VdDropdown, VdDropdownItem, VdTooltip } from "@voodoo.io/voodoo-ui";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { ref, computed, watch } from "vue";

import { useAbtestsLayerSelectQuery, useDeleteLayerMutation, useMutationResponse, Layer, queryNames } from "@/graphql";
import useMessages from "@/hooks/messages";
import usePermissions from "@/hooks/use-permissions";

import AbtestLayerDialog from "../AbtestLayerDialog/AbtestLayerDialog.vue";
import LayerLockedClasses from "./LayerLockedClasses.vue";

interface SelectChoice {
  label: string;
  value: string;
  disabled?: boolean;
  isEditable?: boolean;
  canBeDeleted?: boolean;
}

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },

  versionId: {
    type: String,
    required: true,
  },

  modelValue: {
    type: String,
    required: true,
  },

  isFilter: {
    type: Boolean,
    required: false,
    default: false,
  },

  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const { canWriteAbtests } = usePermissions();

const { confirm } = useMessages();
const { mutate: deleteLayer } = useDeleteLayerMutation({
  refetchQueries: [queryNames.AbtestsLayerSelect],
});

const { result, loading } = useAbtestsLayerSelectQuery({ gameId: props.gameId, versionId: props.versionId });
const layers = computed(() => result.value?.game?.version?.layers ?? []);
const layersList = computed(() => layers.value.map((l) => l.name));
const selectedLayer = ref(props.modelValue);

const isLayerDialogVisible = ref(false);
const layerId = ref<string | undefined>(undefined);

const layerChoices = computed(() => {
  const formattedLayers = layers.value
    .map((layer: Layer) => ({
      label: layer.name,
      value: layer.id,
      default: layer.default,
      isEditable: (!layer.default && !props.onlyShopLayer) ?? false,
      canBeDeleted: !layer.abtestsCount && !layer.default && !props.onlyShopLayer,
    }))
    .sort((a, b) => {
      /**
       * Sorting choices with "Create -> All Layers -> Default -> ...layers in alphabetical order"
       */
      if (a.default) {
        return -1;
      }
      if (b.default) {
        return 1;
      }
      return a.label.localeCompare(b.label);
    });
  const choices = [
    { label: "Create new layer", value: "", isEditable: false, canBeDeleted: false },
    ...formattedLayers,
  ];

  /**
   * If we're using the select as a filter, we want
   * to be able to select All layers at once, so let's add this option.
   */
  if (props.isFilter) {
    choices.splice(1, 0, { label: "All Layers", value: "all", isEditable: false, canBeDeleted: false });
  }

  return choices;
});

/**
 * Layers methods
 */
const onAddLayer = () => {
  layerId.value = undefined;
  isLayerDialogVisible.value = true;
};

/**
 * Delete a layer, but ask before if it's ok 😁
 */
const onDeleteLayer = async (layerChoice: SelectChoice) => {
  const confirmed = await confirm(`Do you really want to delete the layer "${layerChoice.label}"?`);
  if (confirmed) {
    const response = await deleteLayer({
      gameId: props.gameId,
      versionId: props.versionId,
      layerId: layerChoice.value,
    });
    useMutationResponse(response, (resp) => resp?.deleteLayer);
    /**
     * Go back to all layers filter once you deleted a layer
     */
    emit("update:modelValue", "all");
  }
};

const onEditLayer = (id: string) => {
  layerId.value = id;
  isLayerDialogVisible.value = true;
};

const onUpdateSelectedLayer = (newSelectedLayer: string) => {
  selectedLayer.value = newSelectedLayer;
  emit("update:modelValue", newSelectedLayer);
};

watch(
  [() => props.onlyShopLayer],
  () => {
    const newSelectedLayer = layerChoices.value[1].value;
    onUpdateSelectedLayer(newSelectedLayer);
  },
  { deep: true }
);

const onCloseDialog = (id?: string) => {
  isLayerDialogVisible.value = false;

  if (id) {
    onUpdateSelectedLayer(id);
  }
};
</script>

<template>
  <div :class="['select-info', { reverse: !isFilter }]">
    <LayerLockedClasses :layers="layers" :selected-layer="selectedLayer" />
    <section class="layers-select">
      <VdSelect
        v-loading="loading"
        :model-value="selectedLayer"
        :choices="layerChoices"
        :placeholder="isFilter ? 'Filter layers' : 'Choose the layer of the AB Test'"
        :loading="loading"
        prop="layer"
        class="layer-field"
        :disabled="disabled"
        @update:model-value="onUpdateSelectedLayer"
      >
        <template #item="{ item }">
          <div v-if="layerChoices.indexOf(item) === 0" class="new-layer" @click="onAddLayer">
            <VdIcon store="fa" name="plus" />
            <span>{{ item.label }}</span>
          </div>
          <div class="existing-layer">
            <span>{{ item.label }}</span>
            <VdDropdown v-if="item.isEditable && canWriteAbtests">
              <VdDropdownItem @click="() => onEditLayer(item.value)">
                <VdIcon store="fa" name="pen-to-square" />
                <span>Edit</span>
              </VdDropdownItem>
              <VdDropdownItem dangerous :disabled="!item.canBeDeleted" @click="onDeleteLayer(item)">
                <VdTooltip
                  :disabled="item.canBeDeleted"
                  content="Delete or archive all A/B Tests in this layer to be able to delete it."
                >
                  <span>
                    <VdIcon store="fa" name="eraser" color="var(--vd-color-danger)" />
                    <span>Delete</span>
                  </span>
                </VdTooltip>
              </VdDropdownItem>
            </VdDropdown>
          </div>
        </template>
      </VdSelect>
    </section>
  </div>
  <AbtestLayerDialog
    :game-id="gameId"
    :version-id="versionId"
    :layer-id="layerId"
    :visible="isLayerDialogVisible"
    :layers-list="layersList"
    @close="onCloseDialog"
  />
</template>

<style lang="scss" scoped>
.select-info {
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  column-gap: 10px;

  &.reverse {
    flex-direction: row-reverse;
  }
}

.existing-layer,
.new-layer {
  display: flex;
  align-items: center;
}

.existing-layer {
  justify-content: space-between;
}

.new-layer {
  color: $intense-blue;

  svg {
    color: $intense-blue;
    margin-right: size("XS");
  }
}
</style>
